'use client';

import React, { type HTMLAttributes } from 'react';
import { css } from 'styled-components';

import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import { useTranslation } from '@/app/components/translations/Translation';
import GuestReviews from '@/app/components/hotel-detail/guest-reviews/GuestReviews';
import { useHasGuestReviews } from '@/app/components/hotel-detail/hooks/useFetchGuestReviews';
import { isTripAdvisorSource } from '@/app/common/reviews/reviewSource';
import customCss from './css/customCss';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { useIsMobile } from './hooks/useMediaSSR';

type HotelPageGuestReviewsSectionProps = {
  /** Render in the Evo details modal's Reviews tab, below the TripAdvisor/TrustYou reviews (MPH-602). */
  inline?: boolean;
} & HTMLAttributes<HTMLElement>;

export default function HotelPageGuestReviewsSection(props: HotelPageGuestReviewsSectionProps) {
  const { inline = false, ...rest } = props;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { data: staticData } = useFetchHotelStatic();
  // Temporal: show guest reviews in this section for all devices. TODO Remove component in Hero
  // component if finally no longer needed
  // The fr_FR gate lives in useFetchGuestReviews (skips the fetch, so no data arrives here).
  const showGuestReviews = useHasGuestReviews();
  // Inline, the reviews above already carry the "Guest reviews" heading, so this block is titled
  // "Latest reviews" at every width, as one more section of the tab.
  const title = inline
    ? t('vacation.select.details.guest.reviews.latest')
    : isMobile === true
      ? t('vacation.select.details.guest.reviews.title')
      : undefined;
  // They sit right under the hotel's own reviews, so on a TripAdvisor hotel they rate on TA's scale too;
  // TrustYou scores out of 10, like the provider.
  const ratingScale = isTripAdvisorSource(staticData?.hotelOpinion.reviewSource) ? 5 : 10;

  return showGuestReviews ? (
    <HotelPageContentSection
      {...rest}
      title={title}
      css={css`
        display: flex;
        flex-direction: column;
        background-color: white;

        @media ${customCss.breakpoints.sm} {
          background-color: inherit;
        }
      `}
    >
      <GuestReviews inline={inline} ratingScale={ratingScale} />
    </HotelPageContentSection>
  ) : null;
}
